import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { catchError, firstValueFrom, map, of, throwError } from 'rxjs';
import { Patient } from '../../interfaces/patient';
import { PersonFilter } from '../../interfaces/person-filter.type';
import { HraInfo } from '../../interfaces/hra-info';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  protected apiVersion = environment.apiVersion;
  protected apiURL = environment.apiUrl;

  constructor(
    protected httpClient: HttpClient,
    private localStorageService: LocalStorageService,
  ) {
    this.apiURL = this.apiURL + '/v' + this.apiVersion;
  }

  /** Dependente de algum plano (SIM_IS_DEPENDENTE). Falha vira null: a ficha só não mostra o campo. */
  public async ehDependente(cdPessoaFisica: number): Promise<boolean | null> {
    try {
      const resposta = await firstValueFrom(
        this.httpClient.get<any>(`${this.apiURL}/Patient/dependente/${cdPessoaFisica}`),
      );
      const valor = resposta?.data?.[0]?.dependente;
      return typeof valor === 'boolean' ? valor : null;
    } catch {
      return null;
    }
  }

  public async getPatientByCPF(document: string): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Patient/by-document/${document}`)
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getPatientByName(
    name: string,
    pageNumber: number,
    pageSize: number,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(
          `${this.apiURL}/Patient/by-name/${name}?name=&pageNumber=${pageNumber}&pageSize=${pageSize}`,
        )
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getSchedulesByPatient(
    patientId: number,
    clinicId: number,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(
          `${this.apiURL}/Patient/schedules-by-patient/${patientId}/clinic/${clinicId}`,
        )
        .pipe(
          map((response) => response.data),
          catchError(this.handleError),
        ),
    );
  }

  public async getPersonByFilter(filter: PersonFilter): Promise<any> {
    let type = '';

    switch (filter.type) {
      case 0:
        type = 'rne';
        break;
      case 1:
        type = 'passport';
        break;
      case 2:
        type = 'code';
        break;
    }

    return await firstValueFrom(
      this.httpClient
        .get<any>(
          `${this.apiURL}/Patient/person-by-filter?${type}=${filter.filter}`,
        )
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getPersonByDocument(document: string): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Patient/person-by-document/${document}`)
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getVouchersByPersonId(personId: number): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Plan/vouchers-by-person-id/${personId}`)
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getPlanByPersonId(personId: number): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Plan/plan-by-person-id/${personId}`)
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getScheduleAndPriceInfos(
    personId: number,
    scheduleId: number,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(
          `${this.apiURL}/Patient/schedule-and-price-by-person/${personId}/and-by-schedule-id/${scheduleId}`,
        )
        .pipe(
          map((response) => response.data[0]),
          catchError(this.handleError),
        ),
    );
  }

  public async getHraInfo(clientId: number): Promise<HraInfo | null> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/client/hra-info?clientId=${clientId}`)
        .pipe(
          map((response) => response.data[0] as HraInfo),
          catchError(() => of(null)),
        ),
    );
  }

  /**
   * Cadastra paciente que não existe no TASY (pelo CartãoSiM). Se o CPF já existe, o back
   * devolve o paciente existente com jaExistia = true. Erro sobe com a mensagem do back.
   */
  public async cadastrarPaciente(dados: CadastroPacienteDto): Promise<PacienteCadastrado> {
    try {
      const resposta = await firstValueFrom(this.httpClient.post<any>(`${this.apiURL}/Patient/cadastro`, dados));
      const paciente = resposta?.data?.[0];
      if (!paciente) throw { error: resposta };
      return paciente;
    } catch (erro: any) {
      throw new Error(erro?.error?.notifications?.[0]?.message ?? erro?.error?.feedback ?? 'Não foi possível cadastrar o paciente.');
    }
  }

  public async updatePatient(patientUpdated: Patient): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .put(`${this.apiURL}/Patient/update-patient`, {
          id: patientUpdated.id,
          name: patientUpdated.name,
          socialName: patientUpdated.socialName,
          document: patientUpdated.document,
          rne: patientUpdated.rne,
          passport: patientUpdated.passport,
          stranger: patientUpdated.stranger,
          email: patientUpdated.email,
          phone: patientUpdated.phone,
          birthDate: patientUpdated.birthDate,
          maritalStatus: patientUpdated.maritalStatus,
          age: patientUpdated.age,
          gender: patientUpdated.gender,
          address: {
            state: patientUpdated.address.state,
            city: patientUpdated.address.city,
            neighborhood: patientUpdated.address.neighborhood,
            zipcode: patientUpdated.address.zipcode,
            street: patientUpdated.address.street,
            number: patientUpdated.address.number,
            complement: String(patientUpdated.address.complement)
              .replace(/\(.*\)/g, '')
              .slice(0, 40),
          },
          responsibleName: patientUpdated.responsibleName,
          responsibleDocument: patientUpdated.responsibleDocument,
        })
        .pipe(catchError(this.handleError)),
    );
  }

  public async vincularResponsavelFinanceiro(
    cdPessoaFisica: number,
    cpfResponsavel: string,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .post(`${this.apiURL}/Patient/responsavel-financeiro`, {
          cdPessoaFisica,
          cpfResponsavel,
        })
        .pipe(catchError(this.handleError)),
    );
  }

  // ── Fluxo novo (modal igual ao PRE — simcarepos) ──
  public async getResponsavelLegal(cdPessoaFisica: number): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Patient/responsavel-legal/${cdPessoaFisica}`)
        .pipe(
          map((response) => response?.data?.[0] ?? null),
          catchError(() => of(null)),
        ),
    );
  }

  public async salvarResponsavelLegal(
    cdPessoaFisica: number,
    payload: {
      nmContato: string;
      nrCpf: string;
      nrDdd: string;
      nrTelefone: string;
      dsEmail: string;
      dsEndereco: string;
      nrEndereco: string;
      dsComplemento: string;
      dsBairro: string;
      dsMunicipio: string;
      sgEstado: string;
      cdCep: string;
      cdMunicipioIbge: number | null;
    },
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .post(`${this.apiURL}/Patient/responsavel-legal/${cdPessoaFisica}`, payload)
        .pipe(
          // Aqui o motivo real importa (campo faltando, CPF inválido, recusa do Oracle):
          // repassa a mensagem do back em vez de trocá-la pelo texto genérico do status.
          catchError((error: HttpErrorResponse) =>
            throwError(
              () =>
                error.error?.error ??
                error.error?.feedback ??
                'Erro ao salvar responsável legal.',
            ),
          ),
        ),
    );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error occurred';
    if (error.error instanceof ErrorEvent) {
      // Erro do lado do cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Erro do lado do servidor
      switch (error.status) {
        case 400:
          errorMessage = `Bad Request: Verifique os parâmetros enviados.`;
          break;
        case 401:
          errorMessage = `Unauthorized: Requisição não autorizada.`;
          break;
        case 404:
          errorMessage = `Not Found: O recurso solicitado não foi encontrado.`;
          break;
        case 500:
          errorMessage = `Internal Server Error: Houve um problema no servidor. Tente novamente mais tarde.`;
          break;
        default:
          errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
          break;
      }
    }
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}

export interface CadastroPacienteDto {
  clinicId: number;
  cpf: string;
  nome: string;
  nomeSocial?: string | null;
  /** yyyy-MM-dd */
  nascimento: string;
  celular: string;
  /** M, F ou I (domínio 4 do TASY) */
  sexo: string;
  estadoCivil?: string | null;
  email?: string | null;
  cep?: string | null;
  rua?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
}

export interface PacienteCadastrado {
  cdPessoaFisica: number;
  nome: string;
  cpf: string;
  jaExistia: boolean;
}
